import {
  BreadTransaction,
  FirestoreDocRule,
  getNullFieldValueCriterionEquivalent,
  transactionMatchesSpecificRule,
} from "breadcommon";
import { useContext, useRef, useState } from "react";
import { UserContext } from "../firebaseio/UserContext";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { InstitutionsContext } from "../firebaseio/InstitutionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import {
  firestoreDeleteRuleAndUpdateTransactions,
  firestoreUpdateRuleAndUpdateTransactions,
} from "../firebaseio/firestoreIo";
import { InstitutionLogo } from "../common/InstitutionLogo";
import { Checkbox } from "@mui/material";
import { RulePreview } from "../rules_screen/RulesScreen";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "../rye/RyeButton";
import { RyeIcon } from "../rye/RyeIcon";
import { RyeDropDown } from "../rye/RyeDropDown";
import { RyeModalContent } from "../rye/RyeModal";
import { RyeInput } from "../rye/RyeInput";
import { RyeTooltip } from "../rye/RyeTooltip";

export function InlineRuleEditorModal(props: {
  rule: FirestoreDocRule;
  transaction: BreadTransaction;
  ruleForFieldHighlighting: {
    get: FirestoreDocRule | null;
    set: React.Dispatch<React.SetStateAction<FirestoreDocRule | null>>;
  };
  focusTransaction: () => void;
}) {
  const user = useContext(UserContext);
  const accounts = useContext(AccountsContext);
  const institutions = useContext(InstitutionsContext);
  const categories = useContext(CategoriesContext);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const [modifiedRule, setModifiedRule] = useState<FirestoreDocRule>(
    props.rule,
  );

  // There is some complexity here because criteriaIncludesDescription
  // is computed from modifiedRule, so the state setting needs to be done carefully.
  const [descriptionMatchType, _setDescriptionMatchType] = useState(
    modifiedRule.criteria.partial_description == null ? "exact" : "contains",
  );
  const [partialDescription, _setPartialDescription] = useState<string | null>(
    modifiedRule.criteria.partial_description ?? props.transaction.description,
  );

  const criteriaIncludesDescriptionRef = useRef<boolean>(
    props.rule.criteria.description != null ||
      props.rule.criteria.partial_description != null,
  );

  const setDescriptionCriteria = (
    criteriaIncludesDescription: boolean,
    descriptionMatchType: string,
    partialDescription: string | null,
  ) => {
    _setPartialDescription(partialDescription);
    _setDescriptionMatchType(descriptionMatchType);
    if (criteriaIncludesDescription) {
      if (descriptionMatchType === "exact") {
        matchField("description", props.transaction.description);
        ignoreField("partial_description");
      } else {
        matchField("partial_description", partialDescription);
        ignoreField("description");
      }
    } else {
      ignoreField("description");
      ignoreField("partial_description");
    }
  };

  const modifyRule = (field: string, value: string | null) => {
    setModifiedRule((prevRule) => ({
      ...prevRule,
      criteria: {
        ...prevRule.criteria,
        [field]: value,
      },
    }));
  };
  const ignoreField = (field: string) => {
    modifyRule(field, null);
  };
  const matchField = (field: string, value: string | null | undefined) => {
    modifyRule(
      field,
      value == null ? getNullFieldValueCriterionEquivalent() : value,
    );
  };

  const cancel = () => {
    props.ruleForFieldHighlighting.set(null);
  };

  const saveChanges = () => {
    firestoreUpdateRuleAndUpdateTransactions(
      user.uid,
      props.rule.id,
      modifiedRule,
    );
    props.ruleForFieldHighlighting.set(null);
  };

  const deleteRule = () => {
    firestoreDeleteRuleAndUpdateTransactions(user.uid, props.rule.id);
    props.ruleForFieldHighlighting.set(null);
  };

  function getModalContent() {
    return (
      <div
        className={buildClasses("flex", "flex-col", "gap-5", "max-h-[600px]")}
      >
        {transactionMatchesSpecificRule(
          props.transaction,
          modifiedRule,
        ) ? null : (
          <div className={buildClasses("rounded-lg", "p-4", "bg-red-100")}>
            WARNING: your updated rule does not match the selected transaction.
          </div>
        )}
        <SectionCard title="Modify rule criteria">
          <div
            className={buildClasses("flex", "justify-between", "items-center")}
          >
            <div>
              {(() => {
                if (props.transaction.account_id == null) {
                  return "Account is empty";
                }
                const account = accounts.get(
                  props.transaction.account_id,
                );
                if (account) {
                  const institution =
                    account.institution_id && institutions
                      ? institutions.get(account.institution_id)
                      : null;
                  return (
                    <div className="flex items-center, gap-2">
                      Account is
                      <InstitutionLogo institution={institution} size={20} />
                      {account.name}
                    </div>
                  );
                }
                return `Account ID: "${props.transaction.account_id}"`; // Fallback
              })()}
            </div>
            <div>
              <RyeTooltip text="Include in rule criteria">
                <Checkbox
                  checked={modifiedRule.criteria.account_id !== null}
                  onChange={(e) =>
                    e.target.checked
                      ? matchField("account_id", props.transaction.account_id)
                      : ignoreField("account_id")
                  }
                />
              </RyeTooltip>
            </div>
          </div>
          <div
            className={buildClasses("flex", "items-center", "justify-between")}
          >
            <div>
              Merchant is{" "}
              {props.transaction.merchant?.name == null
                ? "empty"
                : `exactly "${props.transaction.merchant?.name}"`}
            </div>
            <div>
              <RyeTooltip text="Include in rule criteria">
                <Checkbox
                  checked={modifiedRule.criteria.merchant_name !== null}
                  onChange={(e) =>
                    e.target.checked
                      ? matchField(
                          "merchant_name",
                          props.transaction.merchant?.name,
                        )
                      : ignoreField("merchant_name")
                  }
                />
              </RyeTooltip>
            </div>
          </div>
          <div
            className={buildClasses("flex", "items-center", "justify-between")}
          >
            <div>
              Type is{" "}
              {props.transaction.type == null
                ? "empty"
                : `exactly "${props.transaction.type}"`}
            </div>
            <div>
              <RyeTooltip text="Include in rule criteria">
                <Checkbox
                  checked={modifiedRule.criteria.type !== null}
                  onChange={(e) =>
                    e.target.checked
                      ? matchField("type", props.transaction.type)
                      : ignoreField("type")
                  }
                />
              </RyeTooltip>
            </div>
          </div>
          <div
            className={buildClasses("flex", "items-center", "justify-between")}
          >
            <div>
              Subtype is{" "}
              {props.transaction.subtype == null
                ? "empty"
                : `exactly "${props.transaction.subtype}"`}
            </div>
            <div>
              <RyeTooltip text="Include in rule criteria">
                <Checkbox
                  checked={modifiedRule.criteria.subtype !== null}
                  onChange={(e) =>
                    e.target.checked
                      ? matchField("subtype", props.transaction.subtype)
                      : ignoreField("subtype")
                  }
                />
              </RyeTooltip>
            </div>
          </div>
          {
            <div
              className={buildClasses(
                "flex",
                "items-center",
                "justify-between",
              )}
            >
              <div
                className={buildClasses(
                  "flex",
                  "items-center",
                  "justify-between",
                  "flex-grow",
                )}
              >
                Description
                {props.transaction.description == null ? (
                  " is empty"
                ) : (
                  <div
                    className={buildClasses(
                      "flex",
                      "items-center",
                      "justify-between",
                      "ml-5",
                      "flex-grow",
                    )}
                  >
                    <RyeDropDown
                      initEntryId={descriptionMatchType}
                      entries={[
                        { id: "exact", text: "is exactly", icon: null },
                        { id: "contains", text: "contains", icon: null },
                      ]}
                      onSelectEntry={(entry) =>
                        setDescriptionCriteria(
                          criteriaIncludesDescriptionRef.current,
                          entry.id,
                          partialDescription,
                        )
                      }
                      width="sm"
                    />
                    <div className="w-5" />
                    {descriptionMatchType === "exact" ? (
                      <RyeTooltip text={props.transaction.description ?? ""}>
                        <div className="flex-grow truncate">
                          {props.transaction.description}
                        </div>
                      </RyeTooltip>
                    ) : (
                      <div
                        className={buildClasses(
                          "flex",
                          "items-center",
                          "h-10",
                          "flex-grow",
                        )}
                      >
                        <RyeInput
                          startingText={partialDescription ?? ""}
                          placeholderText="Enter text..."
                          width="full"
                          onChange={(newText) =>
                            setDescriptionCriteria(
                              criteriaIncludesDescriptionRef.current,
                              descriptionMatchType,
                              newText,
                            )
                          }
                        />
                        {partialDescription !==
                          props.transaction.description && (
                          <RyeIcon
                            name="replay"
                            className={buildClasses(
                              "flex-grow-0",
                              "px-2",
                              "text-on-surface-500",
                              "cursor-pointer",
                            )}
                            tooltip="Reset to the transaction's description"
                            onClick={() =>
                              setDescriptionCriteria(
                                criteriaIncludesDescriptionRef.current,
                                descriptionMatchType,
                                props.transaction.description,
                              )
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="ml-2">
                <RyeTooltip text="Include in rule criteria">
                  <Checkbox
                    checked={criteriaIncludesDescriptionRef.current}
                    onChange={(e) => {
                      criteriaIncludesDescriptionRef.current = e.target.checked;
                      setDescriptionCriteria(
                        criteriaIncludesDescriptionRef.current,
                        descriptionMatchType,
                        partialDescription,
                      );
                    }}
                  />
                </RyeTooltip>
              </div>
            </div>
          }
          <div className="h-5" />
          <div className={buildClasses("bg-surface-300", "p-3", "rounded-md")}>
            <div className={buildClasses("font-medium")}>Preview</div>
            <div className="h-2" />
            <RulePreview
              rule={modifiedRule}
              categories={categories}
              accounts={accounts}
              institutions={institutions}
            />
          </div>
        </SectionCard>
        <SectionCard title="Delete rule">
          <div
            className={buildClasses(
              "flex",
              "justify-between",
              "items-center",
              "gap-20",
            )}
          >
            <div>
              This will permanently delete the rule. This action cannot be
              undone.
            </div>
            <div className={buildClasses("flex-shrink-0")}>
              <RyeButton
                text="Delete Rule"
                vibe="danger"
                variant="outlined"
                onClick={() => setShowDeleteConfirmation(true)}
                icon="delete"
              />
            </div>
          </div>
        </SectionCard>
      </div>
    );
  }

  if (showDeleteConfirmation) {
    return (
      <RyeModalContent
        content={null}
        title="Are you sure you want to delete this rule?"
        actionButtons={[
          {
            text: "Cancel",
            variant: "outlined",
            action: cancel,
            vibe: "subdued",
          },
          {
            text: "Delete rule",
            vibe: "danger",
            variant: "filled",
            action: deleteRule,
          },
        ]}
        alignActionButtons="center"
        onClose={props.focusTransaction}
        onCloseX={cancel}
      />
    );
  }

  return (
    <RyeModalContent
      content={getModalContent()}
      title="Edit Rule"
      actionButtons={[
        {
          text: "Cancel",
          variant: "outlined",
          action: cancel,
          vibe: "subdued",
        },
        {
          text: "Save",
          vibe: "primary",
          variant: "filled",
          disabled: Object.values(modifiedRule.criteria).every(
            (criteria) => criteria == null,
          ),
          action: saveChanges,
        },
      ]}
      alignActionButtons="end"
      onClose={props.focusTransaction}
      onCloseX={cancel}
    />
  );
}

// Define SectionCard component
const SectionCard = (props: {
  title: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={buildClasses(
        "border",
        "border-surface-500",
        "rounded-md",
        "p-4",
        "w-full",
      )}
    >
      {props.children}
    </div>
  );
};
